/* A post in the feed.
 *
 * The client's wireframe specifies the three actions and their order:
 * Like · Comment · Share (docs/CLIENT_TERMINOLOGY.md).
 *
 * THE FOOTER IS TWO ROWS, as in every app people already use: the reaction
 * chips and the comment count above a divider, the three actions below it.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ThumbsUp,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Globe,
  Users,
  Star,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { cn, compactCount, timeAgo } from '@/lib/utils'
import {
  useCanModerate,
  useDeletePost,
  useEditPost,
  useReactToPost,
} from '@/api/queries'
import { CommentThread } from './CommentThread'
import { EditDialog } from './EditDialog'
import { MediaGrid } from './MediaGrid'
import {
  ReactionPicker,
  ReactionSummary,
  ReactorsDialog,
  reactionByKind,
  useReactionTrigger,
} from './Reactions'
import type { Post } from '@/types/api'

export function PostCard({
  post,
  viewerId,
  focusComment,
  highlighted = false,
}: {
  post: Post
  viewerId?: string
  /** Arriving from a "replied to you" tap — open the thread on that comment. */
  focusComment?: string | null
  /** Ringed, because a notification sent the reader to this one. The feed
   *  carries on around it, so without a mark there is nothing to say which
   *  post was meant. */
  highlighted?: boolean
}) {
  // Already open when the reader was sent here for one particular reply.
  const [showComments, setShowComments] = useState(Boolean(focusComment))

  /* SHARING A POST — the wireframe's word, and the same act on both apps.
   *
   * navigator.share is the browser's own sheet, and on a phone browser it is
   * the one that reaches WhatsApp. It only exists on some browsers and only
   * over HTTPS, so the clipboard is the fallback rather than the exception —
   * on a desktop it is what almost everybody gets, and copying a link is what
   * they were going to do anyway.
   *
   * NOT AN IN-APP FORWARD. Sending to another member inside the product needs
   * messaging, and there is none yet; offering half of it would repeat the
   * mistake this button is fixing. */
  const [sent, setSent] = useState(false)

  async function sendPost() {
    const url = `${window.location.origin}/?post=${post.id}`
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Connect In God', text: post.body, url })
        return
      }
      await navigator.clipboard.writeText(url)
      setSent(true)
      // Long enough to read, short enough that the button is a button again
      // before anybody wants to press it twice.
      setTimeout(() => setSent(false), 2000)
    } catch {
      // A dismissed share sheet throws, and so does a clipboard the browser
      // refused. Neither is worth an error message for something the reader
      // can simply try again.
    }
  }
  const [showReactors, setShowReactors] = useState(false)
  const [editing, setEditing] = useState(false)
  const react = useReactToPost()
  const editPost = useEditPost()
  const deletePost = useDeletePost()
  const canModerate = useCanModerate()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const picker = useReactionTrigger()

  const isAuthor = viewerId === post.author.id
  // Editing is the author's alone; removing is moderation, and belongs to
  // whoever is responsible for the room. See useCanModerate.
  const canRemove = isAuthor || canModerate(post.group_id)
  const chosen = reactionByKind(post.viewer_reaction)
  const hasCounts = post.reaction_total > 0 || post.comment_count > 0

  function send(kind: string) {
    react.mutate({ postId: post.id, kind })
    picker.close()
  }

  return (
    <Card
      className={cn(
        'animate-fade-in',
        // A ring rather than a tint: the card already carries colour of its
        // own — photos, feeling chips — and a wash fights all of it.
        highlighted && 'ring-2 ring-brand',
      )}
    >
      {/* ---------------------------------------------------------- header */}
      <div className="flex items-start gap-s3 p-s4 pb-s3">
        <Avatar name={post.author.name} src={post.author.avatar_url} />

        <div className="min-w-0 flex-1">
          <div className="truncate text-md leading-tight">
            <span className="font-semibold">{post.author.name}</span>
            {/* Reads as one sentence, so the name and the feeling are not two
                competing pieces of bold. */}
            {post.feeling ? (
              <span className="font-normal text-ink-2">
                {' '}— feeling {post.feeling.label}{' '}
                <span aria-hidden>{post.feeling.emoji}</span>
              </span>
            ) : null}
            {/* A testimony is a different kind of thing from a post — it is
                the answer to a prayer, and the reader should know before the
                first line. Green because Answered is green everywhere in
                Prayer. Linked to the request it grew out of when it has one. */}
            {post.post_type === 'testimony' ? (
              post.prayer_request_id ? (
                <Link
                  to={`/prayer?request=${post.prayer_request_id}`}
                  className="ml-s2 inline-flex items-center gap-1 rounded-pill bg-pr-green-soft px-s2 py-0.5 align-middle text-xs font-bold text-pr-green-ink hover:underline"
                  title="From an answered Prayer Request"
                >
                  <Star className="h-3 w-3" aria-hidden />
                  Testimony
                </Link>
              ) : (
                <span className="ml-s2 inline-flex items-center gap-1 rounded-pill bg-pr-green-soft px-s2 py-0.5 align-middle text-xs font-bold text-pr-green-ink">
                  <Star className="h-3 w-3" aria-hidden />
                  Testimony
                </span>
              )
            ) : null}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-ink-2">
            <span>{timeAgo(post.created_at)}</span>
            {/* Beside the time, not instead of it: when something was said
                still matters to the replies underneath it. */}
            {post.edited_at ? (
              <span className="text-ink-3" title="This post was edited">
                · edited
              </span>
            ) : null}
            <span aria-hidden>·</span>
            {/* A group post is mixed into the home feed alongside public ones,
                so the group's name is what tells the reader who else can see
                it. Without the badge the audience is ambiguous. */}
            {post.visibility === 'group' ? (
              <Link
                to={`/groups/${post.group_id}`}
                className="flex items-center gap-1 font-semibold text-brand hover:underline"
                title={post.group_name ?? 'Group'}
              >
                <Users className="h-3 w-3" aria-hidden />
                <span className="max-w-[12rem] truncate">
                  {post.group_name ?? 'Group'}
                </span>
              </Link>
            ) : post.visibility === 'church' ? (
              <span className="flex items-center gap-1" title={post.church_name ?? 'Church'}>
                <Users className="h-3 w-3" aria-hidden />
                <span className="max-w-[12rem] truncate">
                  {post.church_name ?? 'Church'}
                </span>
              </span>
            ) : (
              <Globe className="h-3 w-3" aria-label="Everyone" />
            )}
          </div>
        </div>

        {isAuthor || canRemove ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button variant="ghost" size="icon" aria-label="Post options">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={4}
                className="z-50 min-w-[10rem] rounded-md bg-surface p-s1 shadow-3"
              >
                {/* EDIT SITS ABOVE REMOVE, and is the ordinary weight while
                    Remove is red. The common case is fixing a typo; the
                    destructive one should take a deliberate extra beat to
                    reach. */}
                {isAuthor ? (
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                    onSelect={() => setEditing(true)}
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                    Edit post
                  </DropdownMenu.Item>
                ) : null}
                {canRemove ? (
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base text-danger outline-none hover:bg-danger-soft"
                    onSelect={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    Remove post
                  </DropdownMenu.Item>
                ) : null}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : null}
      </div>

      <Confirm
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Remove this post?"
        body="It disappears for everyone who can see it. This cannot be undone."
        confirmLabel="Remove post"
        loading={deletePost.isPending}
        onConfirm={() => deletePost.mutate(post.id)}
      />

      {editing ? (
        <EditDialog
          title="Edit post"
          initialBody={post.body}
          initialPhotos={post.media ?? []}
          initialFeeling={post.feeling?.kind ?? null}
          placeholder="What would you like to say?"
          saving={editPost.isPending}
          error={
            editPost.isError
              ? ((editPost.error as Error)?.message ?? 'That did not save.')
              : null
          }
          onClose={() => setEditing(false)}
          onSave={(edit) =>
            editPost.mutate(
              {
                postId: post.id,
                body: edit.body,
                media_ids: edit.mediaIds,
                feeling: edit.feeling,
                clear_feeling: edit.clearFeeling,
              },
              { onSuccess: () => setEditing(false) },
            )
          }
        />
      ) : null}

      {/* ------------------------------------------------------------ body */}
      {/* whitespace-pre-wrap keeps the line breaks someone typed; break-words
          stops a pasted URL from widening the whole column. */}
      {post.body ? (
        <p className="whitespace-pre-wrap break-words px-s4 pb-s3 text-body">
          {post.body}
        </p>
      ) : null}

      {/* Full-bleed, as every feed does it — an inset photo reads as an
          attachment rather than the content. The card clips, so the photo
          takes its corner rather than squaring it off. */}
      {post.live ? <LiveStrip post={post} /> : null}
      <MediaGrid media={post.media ?? []} />

      {/* ---------------------------------------------------------- counts */}
      {hasCounts ? (
        <div className="flex items-center justify-between px-s4 pb-s2 text-sm text-ink-2">
          {/* The empty span holds the left edge when nothing has been
              reacted, so the comment count stays on the right. */}
          {post.reaction_total > 0 ? (
            <ReactionSummary post={post} onOpen={() => setShowReactors(true)} />
          ) : (
            <span />
          )}

          {post.comment_count > 0 ? (
            <button
              className="hover:underline"
              onClick={() => setShowComments((v) => !v)}
            >
              {compactCount(post.comment_count)}{' '}
              {post.comment_count === 1 ? 'comment' : 'comments'}
            </button>
          ) : null}
        </div>
      ) : null}

      {showReactors ? (
        <ReactorsDialog post={post} onClose={() => setShowReactors(false)} />
      ) : null}

      {/* --------------------------------------------------------- actions */}
      <div className="mx-s4 flex border-t border-line-2 py-1">
        {/* `relative` is what the picker positions against — it opens above
            this button, not above the card. */}
        <div className="relative flex flex-1">
          {picker.open ? (
            <ReactionPicker
              selected={post.viewer_reaction}
              onPick={send}
              surfaceProps={picker.surfaceProps}
            />
          ) : null}

          <Button
            variant="action"
            {...picker.triggerProps}
            // The plain click IS the 'like' kind, and goes down the same path
            // as the picker — so clicking Like on a post already marked Pray
            // replaces the Pray, exactly as choosing Like would.
            onClick={picker.guardClick(() => send(chosen?.kind ?? 'like'))}
            aria-pressed={post.liked_by_viewer}
            aria-haspopup="true"
            className={cn(post.liked_by_viewer && 'text-brand')}
          >
            {/* Shown INSTEAD of the icon once a kind is chosen: an emoji and a
                thumbs-up together read as two separate states. */}
            {chosen && chosen.kind !== 'like' ? (
              <span className="text-[18px] leading-none" aria-hidden>
                {chosen.emoji}
              </span>
            ) : (
              <ThumbsUp
                className={cn(
                  'h-[18px] w-[18px]',
                  post.liked_by_viewer && 'fill-brand',
                )}
                aria-hidden
              />
            )}
            {/* The chosen reaction names the button, so the card says what you
                did without needing the stack above it. */}
            {chosen?.label ?? 'Like'}
          </Button>
        </div>

        {post.comments_off ? (
          /* The live's host turned comments off; the server refuses them
             too, so this only saves somebody writing one for nothing. */
          <Button variant="action" disabled title="Comments are turned off for this post">
            <MessageCircle className="h-[18px] w-[18px]" aria-hidden />
            Comments off
          </Button>
        ) : (
          <Button variant="action" onClick={() => setShowComments((v) => !v)}>
            <MessageCircle className="h-[18px] w-[18px]" aria-hidden />
            Comment
          </Button>
        )}

        {/* SHARE — the wireframe's own word, in the wireframe's own order:
            Like · Comment · Share. It was briefly "Send", which is what the
            button does but not what the design calls it, and a product whose
            labels drift from the document it was specified in is one nobody
            can check against that document. */}
        <Button variant="action" onClick={sendPost}>
          <Share2 className="h-[18px] w-[18px]" aria-hidden />
          {sent ? 'Link copied' : 'Share'}
        </Button>
      </div>

      {showComments ? (
        <CommentThread postId={post.id} focusComment={focusComment} />
      ) : null}
    </Card>
  )
}


/** A live event's post: on now, with the way in; or over, with its replay
 *  below as the post's video (or a word that it is on the way). */
function LiveStrip({ post }: { post: Post }) {
  const live = post.live!
  if (live.state === 'ended') {
    if (post.media?.length) return null
    return (
      <p className="mx-s4 mb-s3 rounded bg-surface-2 px-s3 py-s2 text-sm text-ink-2">
        This live has ended. Its replay will appear here shortly.
      </p>
    )
  }
  const now = live.state === 'live'
  return (
    <div
      className={cn(
        'mx-s4 mb-s3 flex items-center gap-s3 rounded px-s3 py-s3',
        now ? 'bg-danger-soft' : 'bg-brand-softer',
      )}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-pill px-s2 py-0.5 text-xs font-bold tracking-wide',
          now ? 'bg-danger text-white' : 'bg-brand text-white',
        )}
      >
        {now ? <span className="h-1.5 w-1.5 animate-pulse rounded-pill bg-white" /> : null}
        {now ? 'LIVE NOW' : 'LIVE SOON'}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
        {now ? 'Happening right now' : 'Starting soon'}
      </span>
      <Button asChild size="sm" variant={now ? 'danger' : 'primary'}>
        <Link to={`/calls/${live.call_id}/live`}>Watch</Link>
      </Button>
    </div>
  )
}
